import React, { useState, useMemo, useCallback } from 'react';
import { Grid } from '@mui/material';
import { useSelector } from 'react-redux';

import DialogMapModal from '@shared/components/Dialogs/Map';
import MapContainer from '@shared/components/MapContainer';
import { findClosestEntity } from '@shared/helpers/findClosestEntity';

import SubsidiaryActions from './SubsidiaryActions';
import SubsidiaryDetails from './SubsidiaryDetails';
import GenericDropdown from '../GenericDropdown';

const SubsidiarySelectorContent = ({
  subsidiaries,
  subsidiary,
  handleSubsidiarie,
  name,
  withLogo = false,
  error = false,
  helperText = '',
  compactDetails = false,
}) => {
  const [showModalMap, setShowModalMap] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const pageConfiguration = useSelector(store => store.pageConfiguration);

  const items = useMemo(
    () =>
      subsidiaries.map(s => ({
        id: s.id,
        fragments: [{ text: s.name, bold: false }],
      })),
    [subsidiaries]
  );

  const selectedItem = useMemo(
    () =>
      subsidiary
        ? {
            id: subsidiary.id,
            fragments: [{ text: subsidiary.name, bold: false }],
          }
        : null,
    [subsidiary]
  );

  const handleSelect = useCallback(
    item => {
      const selected = subsidiaries.find(s => s.id === item.id);
      if (!selected) return;

      handleSubsidiarie(selected);
    },
    [subsidiaries, handleSubsidiarie]
  );

  const handleAutoSelect = useCallback(async () => {
    try {
      setIsLoading(true);
      const closest = await findClosestEntity(subsidiaries);
      handleSubsidiarie(closest);
    } catch (error) {
      console.error('Error geolocalizando:', error);
    } finally {
      setIsLoading(false);
    }
  }, [subsidiaries, handleSubsidiarie]);

  const handleMarkerClick = useCallback(
    marker => {
      handleSubsidiarie(marker);
      setShowModalMap(false);
    },
    [handleSubsidiarie]
  );

  return (
    <Grid
      container
      item
      xs={12}
      id="subsidiary-selector-container"
      rowSpacing={2}
    >
      <Grid
        item
        id="subsidiary-selector-item-container"
        xs={12}
        sm={withLogo ? 6 : 12}
        container
        columnSpacing={1}
        rowSpacing={2}
        sx={{
          mt: '0px',
          '&.MuiGrid-item': {
            pt: 0,
          },
        }}
      >
        <Grid item xs={12}>
          <GenericDropdown
            items={items}
            selectedItem={selectedItem}
            onSelect={handleSelect}
            buttonLabel="Seleccioná tu Punto de Venta"
            hasError={error}
            helperText={helperText}
          />
        </Grid>

        <SubsidiaryActions
          name={name}
          isLoading={isLoading}
          onAutoSelect={handleAutoSelect}
          onMapSelect={() => setShowModalMap(true)}
          withLogo={withLogo}
        />
      </Grid>

      <Grid
        item
        xs={12}
        sm={withLogo ? 6 : 12}
        sx={{
          '&.MuiGrid-item': {
            pt: 0,
          },
        }}
      >
        <SubsidiaryDetails
          subsidiary={subsidiary}
          withLogo={withLogo}
          imageWidth={140}
          compactDetails={compactDetails}
        />
      </Grid>

      <DialogMapModal
        isOpen={showModalMap}
        onClose={() => setShowModalMap(false)}
        title="Seleccione el Punto de Venta:"
      >
        {showModalMap && (
          <MapContainer
            center={{ lat: -31.409898, lng: -64.182171 }}
            multipleMarkers={subsidiaries}
            customMarker={pageConfiguration.site_logos.map}
            onMarkerClick={handleMarkerClick}
          />
        )}
      </DialogMapModal>
    </Grid>
  );
};

export default SubsidiarySelectorContent;
